import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import { fmtInt } from '@/lib/format';
import { Icon, IconName } from '@/components/Icon';
import { ProgressArc } from '@/components/ProgressArc';
import { StatusPill } from '@/components/StatusPill';
import { EmptyState } from '@/components/EmptyState';
import { haptic } from '@/lib/telegram';

const ACTIONS: { to: string; icon: IconName; title: string; sub: string }[] = [
  { to: 'inbox',    icon: 'inbox',    title: 'Inbox',    sub: 'ЛС · пинги · реплаи' },
  { to: 'history',  icon: 'message',  title: 'История',  sub: 'реальные посевы · reply' },
  { to: 'chats',    icon: 'shield',   title: 'Чаты',     sub: 'добавить · оценить' },
  { to: 'accounts', icon: 'users',    title: 'Аккаунты', sub: 'привязать к кампании' },
  { to: 'team',     icon: 'users',    title: 'Участники', sub: 'owner · доступы' },
  { to: 'dialogs',  icon: 'sparkles', title: 'Диалоги',  sub: 'генерация · ревью' },
  { to: 'prompts',  icon: 'message',  title: 'Промпты',  sub: 'бриф · ответы в ЛС' },
  { to: 'launch',   icon: 'play',     title: 'Запуск',   sub: 'расписание · лимиты' },
  { to: 'logs',     icon: 'search',   title: 'Логи',     sub: 'задачи · статусы · ошибки' },
  { to: 'reports',  icon: 'download', title: 'Отчёты',   sub: 'xlsx · история' },
];

export function CampaignDetailPage(): JSX.Element {
  const { id } = useParams();
  const cid = Number(id);
  const { data: campaign, loading, error, reload } = useAsync(() => api.getCampaign(cid), [cid]);
  const { data: targets, reload: reloadTargets } = useAsync(() => api.listTargets(cid), [cid]);
  const nav = useNavigate();

  useEffect(() => {
    if (!campaign) return;
    const shouldPoll =
      campaign.status === 'running' ||
      campaign.status === 'ready' ||
      campaign.seeding.activeTargets > 0;
    if (!shouldPoll) return;

    const timer = window.setInterval(() => {
      reload();
      reloadTargets();
    }, 10_000);

    return () => window.clearInterval(timer);
  }, [campaign, reload, reloadTargets]);

  if (loading) {
    return (
      <div className="detail-page space-y-4">
        <div className="skeleton h-20" />
        <div className="skeleton h-72" />
        <div className="skeleton h-80" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="detail-page space-y-4">
        <button
          className="flex h-14 w-14 items-center justify-center rounded-[22px] border border-white/8 bg-[#0d1225] text-[#c8d2ff] shadow-[0_14px_28px_rgba(0,0,0,0.28)] active:scale-95"
          onClick={() => {
            haptic('light');
            nav('/campaigns');
          }}
          aria-label="Назад"
        >
          <Icon name="chevron-left" size={22} />
        </button>
        <EmptyState
          icon="search"
          title="Кампания недоступна"
          body={error || 'Эта кампания не найдена или больше не принадлежит текущему пользователю.'}
          cta={
            <button
              className="btn-primary"
              onClick={() => {
                haptic('light');
                nav('/campaigns');
              }}
            >
              Вернуться к кампаниям
            </button>
          }
        />
      </div>
    );
  }

  const totalTargets = targets?.length ?? campaign.counts.targets ?? 0;
  const joinedTargets = (targets ?? []).filter((target) => target.isJoined).length;
  const trackedSeedTargets = campaign.seeding.trackedTargets || campaign.seeding.readyTargets;
  const completedSeedTargets = campaign.seeding.completedTargets || 0;
  const postedSeedTargets = campaign.seeding.postedTargets;
  const activeSeedTargets = campaign.seeding.activeTargets;
  const failedSeedTargets = campaign.seeding.failedTargets;
  const targetStatuses = campaign.seeding.targetStatuses || [];
  const joinProgress = totalTargets ? joinedTargets / totalTargets : 0;
  const seedProgress = trackedSeedTargets > 0 ? completedSeedTargets / trackedSeedTargets : 0;
  const activeJoinTasks = Math.max(0, totalTargets - joinedTargets);
  const totalTaskSlots = trackedSeedTargets;
  const completedTasks = completedSeedTargets;
  const overallProgress = seedProgress;
  const activeTasks = activeSeedTargets;

  const phase =
    campaign.status === 'draft' ? 'distribute' :
    campaign.status === 'ready' ? 'launch' :
    campaign.status === 'running' ? 'pause' :
    campaign.status === 'paused' ? 'resume' :
    'done';

  const cta = async () => {
    if (phase === 'distribute') {
      haptic('light');
      nav(`/campaigns/${campaign.id}/launch`);
      return;
    }
    if (phase === 'launch') {
      haptic('light');
      nav(`/campaigns/${campaign.id}/launch`);
      return;
    }
    if (phase === 'pause') {
      haptic('warning');
      await api.setCampaignStatus(campaign.id, 'paused');
      reload();
      return;
    }
    if (phase === 'resume') {
      haptic('success');
      await api.setCampaignStatus(campaign.id, 'running');
      reload();
    }
  };

  const ctaLabel =
    phase === 'distribute' ? 'Открыть запуск' :
    phase === 'launch' ? 'Открыть запуск' :
    phase === 'pause' ? 'Поставить на паузу' :
    phase === 'resume' ? 'Возобновить' :
    'Кампания завершена';

  const ownerLabel =
    campaign.owner?.username
      ? `@${campaign.owner.username}`
      : campaign.owner?.firstName || campaign.owner?.tgId || 'не назначен';

  return (
    <div className="detail-page space-y-5">
      <section className="flex items-center gap-3 pt-1">
        <button
          className="flex h-14 w-14 items-center justify-center rounded-[22px] border border-white/8 bg-[#0d1225] text-[#c8d2ff] shadow-[0_14px_28px_rgba(0,0,0,0.28)] active:scale-95"
          onClick={() => {
            haptic('light');
            nav(-1);
          }}
          aria-label="Назад"
        >
          <Icon name="chevron-left" size={22} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold text-white">{campaign.name}</div>
          <div className="text-[13px] text-[#7a82a7]">
            #{campaign.id} · {campaign.isOwned ? 'моя кампания' : 'командная'} · owner {ownerLabel}
          </div>
        </div>
        <StatusPill status={campaign.status} />
      </section>

      <section className="card-hero p-5">
        <div className="relative z-10 flex items-center gap-4">
          <ProgressArc
            value={seedProgress}
            size={108}
            stroke={9}
            label={String(completedSeedTargets)}
            sublabel={`из ${trackedSeedTargets}`}
          />
          <div className="min-w-0 flex-1">
            <div className="text-[12px] uppercase tracking-[0.22em] text-white/58">посевы</div>
            <div className="mt-2 text-[17px] font-semibold uppercase leading-none text-white">
              чатов засеяно
            </div>
            <div className="mt-3 text-[13px] leading-5 text-white/68">
              обработано {completedSeedTargets} из {trackedSeedTargets} · успешно {postedSeedTargets} · активных {activeSeedTargets} · ошибок {failedSeedTargets}
            </div>
            <button
              onClick={cta}
              disabled={phase === 'done'}
              className={`mt-5 inline-flex rounded-full px-6 py-4 text-[15px] font-semibold transition active:scale-[0.98] ${
                phase === 'pause'
                  ? 'border border-[#a95579]/55 bg-[rgba(91,30,55,0.22)] text-[#ff7f9f]'
                  : 'border border-white/12 bg-[rgba(20,27,54,0.34)] text-white'
              }`}
            >
              <span className="flex items-center gap-2">
                <Icon name={phase === 'pause' ? 'pause' : 'play'} size={16} />
                {ctaLabel}
              </span>
            </button>
          </div>
        </div>
      </section>

      <section className="surface-panel p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[15px] font-semibold text-white">Прогресс задач</div>
            <div className="mt-1 text-[13px] text-[#7e87aa]">
              Активных задач посева: {activeTasks} из {totalTaskSlots || 0}
            </div>
          </div>
          <div className="rounded-full border border-[#5361b9]/40 bg-[#17204a] px-4 py-2 text-[15px] font-semibold text-[#d7deff]">
            {Math.round(overallProgress * 100)}%
          </div>
        </div>

        <div className="mt-6 space-y-5">
          <TaskProgressRow
            label="Готовность чатов"
            value={joinProgress}
            countLabel={`${joinedTargets}/${totalTargets || 0}`}
            note={`${joinedTargets}/${totalTargets || 0} чатов уже обработано`}
            tone="blue"
          />
          <TaskProgressRow
            label="Посевы"
            value={seedProgress}
            countLabel={`${completedTasks}/${trackedSeedTargets || 0}`}
            note={
              trackedSeedTargets > 0
                ? `активных ${activeSeedTargets} · ошибок ${failedSeedTargets} · успешных посевов ${fmtInt(campaign.counts.posts)}`
                : 'ещё нет чатов, готовых к посеву'
            }
            tone="green"
          />
        </div>

        {targetStatuses.length > 0 ? (
          <div className="mt-6 space-y-3">
            <div className="text-[13px] font-semibold uppercase tracking-[0.16em] text-[#7e87aa]">
              Чаты в посеве
            </div>
            <div className="space-y-2">
              {targetStatuses.map((item) => (
                <SeedTargetStatusRow
                  key={item.targetId}
                  title={item.title}
                  normalizedLink={item.normalizedLink}
                  status={item.status}
                  messagesPosted={item.messagesPosted}
                  lastError={item.lastError}
                />
              ))}
            </div>
          </div>
        ) : null}

        <Link
          to={`/campaigns/${campaign.id}/logs`}
          onClick={() => haptic('light')}
          className="soft-outline-button mt-6"
        >
          <Icon name="search" size={18} />
          Открыть логи задач
        </Link>
      </section>

      <section className="grid grid-cols-3 gap-3">
        <MetricCard value={campaign.counts.targets} label="чатов" />
        <MetricCard value={campaign.counts.accounts} label="аккаунтов" />
        <MetricCard value={campaign.counts.dialogs} label="диалогов" />
        <MetricCard value={campaign.counts.posts} label="посевов" />
        <MetricCard value={campaign.counts.pings} label="пингов" />
        <MetricCard value={campaign.counts.dms} label="лс" accent />
      </section>

      <section className="grid grid-cols-2 gap-3">
        {ACTIONS.map((action) => (
          <Link
            key={action.to}
            to={`/campaigns/${campaign.id}/${action.to}`}
            onClick={() => haptic('light')}
            className="menu-tile flex flex-col justify-between p-5 transition active:scale-[0.992]"
          >
            <span className="icon-circle-ghost">
              <Icon name={action.icon} size={20} />
            </span>
            <div>
              <div className="text-[16px] font-semibold text-white">{action.title}</div>
              <div className="mt-1 text-[13px] leading-5 text-[#8088ab]">{action.sub}</div>
            </div>
          </Link>
        ))}
      </section>

      <button
        onClick={() => nav(`/campaigns/${campaign.id}/reports`)}
        className="soft-outline-button"
      >
        <Icon name="download" size={18} />
        Сгенерировать отчёт XLSX
      </button>

      {campaign.accessRole === 'owner' ? (
        <button
          onClick={async () => {
            if (!confirm(`Удалить кампанию «${campaign.name}»? Это действие необратимо.`)) return;
            haptic('warning');
            await api.deleteCampaign(campaign.id);
            nav('/campaigns', { replace: true });
          }}
          className="btn-ghost w-full text-[#f0b3c1]"
        >
          <Icon name="x" size={16} />
          Удалить кампанию
        </button>
      ) : null}
    </div>
  );
}

function TaskProgressRow({
  label,
  value,
  countLabel,
  note,
  tone,
}: {
  label: string;
  value: number;
  countLabel: string;
  note: string;
  tone: 'blue' | 'green';
}): JSX.Element {
  const clamped = Math.max(0, Math.min(1, value));
  const fill =
    tone === 'green'
      ? 'linear-gradient(90deg, #3ed98f 0%, #49d8b2 100%)'
      : 'linear-gradient(90deg, #4a5cff 0%, #7290ff 100%)';

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-[15px] font-semibold text-white">{label}</div>
        <div className="text-[15px] text-[#97a0c4]">{countLabel}</div>
      </div>
      <div className="h-[11px] w-full overflow-hidden rounded-full bg-white/8">
        <div
          className="h-full rounded-full"
          style={{ width: `${clamped * 100}%`, background: fill }}
        />
      </div>
      <div className="mt-2 text-[13px] text-[#747d9f]">{note}</div>
    </div>
  );
}

function SeedTargetStatusRow({
  title,
  normalizedLink,
  status,
  messagesPosted,
  lastError,
}: {
  title: string;
  normalizedLink: string;
  status: 'scheduled' | 'running' | 'posted' | 'failed' | 'cancelled';
  messagesPosted: number;
  lastError: string | null;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 rounded-[20px] border border-white/7 bg-white/[0.035] px-4 py-3">
      <div className="min-w-0">
        <div className="truncate text-[14px] font-semibold text-white">{title}</div>
        <div className="mt-1 truncate text-[12px] text-[#7e87aa]">
          {normalizedLink} · сообщений {messagesPosted}
          {lastError ? ` · ${lastError}` : ''}
        </div>
      </div>
      <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${seedStatusClass(status)}`}>
        {seedStatusLabel(status)}
      </span>
    </div>
  );
}

function seedStatusLabel(status: 'scheduled' | 'running' | 'posted' | 'failed' | 'cancelled') {
  switch (status) {
    case 'scheduled':
      return 'в очереди';
    case 'running':
      return 'идёт';
    case 'posted':
      return 'завершён';
    case 'failed':
      return 'ошибка';
    case 'cancelled':
      return 'отменён';
  }
}

function seedStatusClass(status: 'scheduled' | 'running' | 'posted' | 'failed' | 'cancelled') {
  switch (status) {
    case 'posted':
      return 'bg-emerald-500/15 text-emerald-200';
    case 'running':
      return 'bg-sky-500/15 text-sky-200';
    case 'failed':
      return 'bg-rose-500/15 text-rose-200';
    case 'cancelled':
      return 'bg-amber-500/15 text-amber-200';
    default:
      return 'bg-white/8 text-[#c9d3ff]';
  }
}

function MetricCard({
  value,
  label,
  accent,
}: {
  value: number;
  label: string;
  accent?: boolean;
}): JSX.Element {
  return (
    <div className="metric-card px-4 py-4">
      <div className={`text-[22px] font-bold leading-none ${accent ? 'text-[#92a6ff]' : 'text-white'}`}>
        {fmtInt(value)}
      </div>
      <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-[#71789a]">
        {label}
      </div>
    </div>
  );
}
